import React, { useState, useEffect, useMemo } from 'react';
import {
  BoltIcon,
  BuildingOffice2Icon,
  EyeIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ClockIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  XMarkIcon,
  CalendarDaysIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowRightIcon,
  ChartPieIcon,
  TableCellsIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { energiaService, EnergiaPOD, EnergiaMeasurement, EnergiaDailySummary, EnergiaMonthlySummary, MSCONSBillingRecord } from '../services/energiaService';
import { useLanguage } from '../contexts/LanguageContext';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';


const EnergiaPODs = () => {
  const { t } = useLanguage();
  const [pods, setPods] = useState<EnergiaPOD[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedConnectionStatus, setSelectedConnectionStatus] = useState<string>('');
  const [selectedDataQuality, setSelectedDataQuality] = useState<string>('');
  const [consumptionFilter, setConsumptionFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPod, setSelectedPod] = useState<EnergiaPOD | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(''); // YYYY-MM formátum

  const [sortBy, setSortBy] = useState<string>('last_update');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [measurementData, setMeasurementData] = useState<EnergiaMeasurement[]>([]);
  const [dailySummaries, setDailySummaries] = useState<EnergiaDailySummary[]>([]);
  const [monthlySummaries, setMonthlySummaries] = useState<EnergiaMonthlySummary[]>([]);
  const [msconsBillingRecords, setMsconsBillingRecords] = useState<MSCONSBillingRecord[]>([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'measurements' | 'daily' | 'monthly' | 'reverse_feed' | 'mscons'>('details');
  const [measurementLimit, setMeasurementLimit] = useState<number>(100);
  const [copiedPodId, setCopiedPodId] = useState<string | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);

  // No more static arrays - we use dynamic uniqueProviders instead

  // Analytics section toggles
  const [showProviderAnalytics, setShowProviderAnalytics] = useState(false);
  const [showConsumptionAnalytics, setShowConsumptionAnalytics] = useState(false);
  const [showQualityAnalytics, setShowQualityAnalytics] = useState(false);

  useEffect(() => {
    loadPods();
  }, []); // Load all PODs initially, filter with client-side logic

  const loadPods = async () => {
    try {
      setLoading(true);
      // Load hybrid POD data from both energy_pods and billing_queue collections
      // This ensures we see ALL PODs regardless of their data source
      const hybridPods = await energiaService.getHybridPods();
      setPods(hybridPods);
    } catch (error) {
      console.error('Error loading hybrid POD data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPods = useMemo(() => {
    return pods.filter(pod => {
      const searchMatch =
        pod.pod_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pod.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pod.customer_info?.customer_name && 
          pod.customer_info.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (pod.customer_info?.company_name && 
          pod.customer_info.company_name.toLowerCase().includes(searchTerm.toLowerCase()));

      const providerMatch = !selectedProvider || pod.provider === selectedProvider;

      const connectionMatch = !selectedConnectionStatus || 
        (selectedConnectionStatus === 'connected' && pod.customer_info && pod.customer_info.customer_name !== 'Nincs hozzárendelve') ||
        (selectedConnectionStatus === 'unconnected' && (!pod.customer_info || pod.customer_info.customer_name === 'Nincs hozzárendelve'));

      const qualityMatch = !selectedDataQuality || 
        pod.data_quality?.data_completeness === selectedDataQuality;

      const consumptionMatch = !consumptionFilter || (() => {
        const consumption = pod.total_consumption_kwh;
        switch (consumptionFilter) {
          case 'high': return consumption > 100000;
          case 'medium': return consumption >= 10000 && consumption <= 100000;
          case 'low': return consumption > 0 && consumption < 10000;
          case 'zero': return consumption === 0;
          case 'reverse_feed': return pod.total_reverse_feed_kwh && pod.total_reverse_feed_kwh > 0;
          default: return true;
        }
      })();

      const monthMatch = !selectedMonth || (() => {
        const start = pod.date_range?.start?.toDate ? pod.date_range.start.toDate() : null;
        const end = pod.date_range?.end?.toDate ? pod.date_range.end.toDate() : (pod.last_update?.toDate ? pod.last_update.toDate() : null);
        if (!start || !end) return false;
        const startMonth = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
        const endMonth = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}`;
        return selectedMonth >= startMonth && selectedMonth <= endMonth;
      })();

      return searchMatch && providerMatch && connectionMatch && qualityMatch && consumptionMatch && monthMatch;
    }).sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'pod_id':
          aValue = a.pod_id;
          bValue = b.pod_id;
          break;
        case 'customer_name':
          aValue = a.customer_info?.customer_name || 'zzz';
          bValue = b.customer_info?.customer_name || 'zzz';
          break;
        case 'provider':
          aValue = a.provider;
          bValue = b.provider;
          break;
        case 'consumption':
          aValue = a.total_consumption_kwh;
          bValue = b.total_consumption_kwh;
          break;
        case 'reverse_feed':
          aValue = a.total_reverse_feed_kwh || 0;
          bValue = b.total_reverse_feed_kwh || 0;
          break;
        case 'cost':
          aValue = a.total_cost_huf;
          bValue = b.total_cost_huf;
          break;
        case 'measurements':
          aValue = a.measurement_count;
          bValue = b.measurement_count;
          break;
        case 'last_update':
        default:
          aValue = a.last_update?.seconds || 0;
          bValue = b.last_update?.seconds || 0;
          break;
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
    });
  }, [pods, searchTerm, selectedProvider, selectedConnectionStatus, selectedDataQuality, consumptionFilter, selectedMonth, sortBy, sortOrder]);

  // Calculate unique values for filters
  const uniqueProviders = useMemo(() => {
    return Array.from(new Set(pods.map(pod => pod.provider))).sort();
  }, [pods]);

  const uniqueCustomers = useMemo(() => {
    return Array.from(new Set(
      pods
        .filter(pod => pod.customer_info?.customer_name && pod.customer_info.customer_name !== 'Nincs hozzárendelve')
        .map(pod => pod.customer_info!.customer_name)
    )).sort();
  }, [pods]);

  const availableMonths = useMemo(() => {
    const months = new Set<string>();
    pods.forEach(pod => {
      // Use date_range to generate all months in the data range
      const start = pod.date_range?.start?.toDate ? pod.date_range.start.toDate() : null;
      const end = pod.date_range?.end?.toDate ? pod.date_range.end.toDate() : (pod.last_update?.toDate ? pod.last_update.toDate() : null);
      if (start && end) {
        const current = new Date(start.getFullYear(), start.getMonth(), 1);
        const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
        while (current <= endMonth) {
          const monthKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
          months.add(monthKey);
          current.setMonth(current.getMonth() + 1);
        }
      } else if (pod.last_update?.toDate) {
        const date = pod.last_update.toDate();
        months.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
      }
    });
    return Array.from(months).sort().reverse(); // Legújabb hónapok elől
  }, [pods]);

  const totalConsumption = filteredPods.reduce((sum, pod) => sum + (pod.total_consumption_kwh || 0), 0);
  const totalCost = filteredPods.reduce((sum, pod) => sum + (pod.total_cost_huf || 0), 0);

  // Pagination calculations
  const totalPages = Math.ceil(filteredPods.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPagePods = filteredPods.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredPods.length, itemsPerPage]);

  const handleViewDetails = (pod: EnergiaPOD) => {
    setSelectedPod(pod);
    setActiveTab('details');
    loadMeasurementData(pod.id);
  };

  const loadMeasurementData = async (podId: string, limit?: number) => {
    setLoadingMeasurements(true);
    try {
      const actualLimit = limit || measurementLimit;
      const [measurements, summaries, monthlies, msconsRecords] = await Promise.all([
        actualLimit === 99999 
          ? energiaService.getAllPodMeasurements(podId)
          : energiaService.getPodMeasurements(podId, actualLimit),
        energiaService.getPodDailySummaries(podId, 400),
        energiaService.getMonthlyPodSummaries(podId, 13),
        energiaService.getMSCONSBillingRecords(podId)
      ]);
      setMeasurementData(measurements);
      setDailySummaries(summaries);
      setMonthlySummaries(monthlies);
      setMsconsBillingRecords(msconsRecords);
    } catch (error) {
      console.error('Error loading measurement data:', error);
      setMeasurementData([]);
      setDailySummaries([]);
      setMonthlySummaries([]);
      setMsconsBillingRecords([]);
    } finally {
      setLoadingMeasurements(false);
    }
  };

  const getProviderColor = (provider: string) => {
    const colors: Record<string, string> = {
      'EON': 'bg-orange-100 text-orange-800',
      'MVMI': 'bg-indigo-100 text-indigo-800',
      'OPUS': 'bg-red-100 text-red-800',
      'EMASZ': 'bg-purple-100 text-purple-800',
      'ELMU': 'bg-green-100 text-green-800',
      // Legacy support (case-insensitive fallback)
      'demasz': 'bg-blue-100 text-blue-800',
      'elmu': 'bg-green-100 text-green-800',
      'emasz': 'bg-purple-100 text-purple-800',
      'eon': 'bg-orange-100 text-orange-800',
      'eon-mvm': 'bg-amber-100 text-amber-800',
      'opus': 'bg-red-100 text-red-800',
      'mvmi': 'bg-indigo-100 text-indigo-800'
    };
    return colors[provider] || 'bg-gray-100 text-gray-800';
  };

  const getCustomerTypeColor = (type?: string) => {
    return type === 'organization' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800';
  };

  const getDataQualityColor = (quality?: string) => {
    switch (quality) {
      case 'good': return 'bg-green-100 text-green-800';
      case 'limited': return 'bg-yellow-100 text-yellow-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDataQualityIcon = (quality?: string) => {
    switch (quality) {
      case 'good': return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'limited': return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />;
      case 'poor': return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
      default: return <SignalIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTrendIcon = (direction?: string) => {
    switch (direction) {
      case 'increasing': return <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />;
      case 'decreasing': return <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />;
      case 'stable': return <ArrowRightIcon className="w-4 h-4 text-gray-500" />;
      default: return null;
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // State for storing monthly summaries for tooltips
  const [podMonthlyData, setPodMonthlyData] = useState<Map<string, EnergiaMonthlySummary[]>>(new Map());

  const getLastMonthTooltip = (pod: EnergiaPOD) => {
    const monthlies = podMonthlyData.get(pod.id);
    
    if (!monthlies || monthlies.length === 0) {
      return `📅 Havi adatok betöltése...\n(Kattints a POD-ra a részletekért)`;
    }
    
    // Get last 3 months
    const recentMonths = monthlies.slice(0, 3);
    
    return recentMonths.map((m) => {
      return `📅 ${m.year}. ${String(m.monthNumber).padStart(2, '0')}.\n` +
             `⚡ Fogyasztás: ${energiaService.formatKWH(m.total_consumption_kwh)}\n` +
             `${m.total_reverse_feed_kwh > 0 ? `☀️ Visszatáplálás: ${energiaService.formatKWH(m.total_reverse_feed_kwh)}\n` : ''}` +
             `💰 Költség: ${energiaService.formatHUF(m.total_cost_huf)}\n` +
             `📊 Átlagár: ${energiaService.formatHUF(m.avg_price_huf_kwh)} / kWh`;
    }).join('\n\n');
  };

  // Load monthly data for visible PODs on hover
  const handlePodHover = async (podId: string) => {
    if (podMonthlyData.has(podId)) return; // Already loaded
    
    try {
      const monthlies = await energiaService.getMonthlyPodSummaries(podId, 13);
      setPodMonthlyData(prev => new Map(prev).set(podId, monthlies));
    } catch (error) {
      console.error('Error loading monthly data for tooltip:', error);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4 xl:space-y-6 pb-20 sm:pb-0 px-2 xl:px-0">
      {/* Header */}
      <CommonPageHeader
        title="Energia POD Management"
        subtitle="POD adatok és fogyasztás kezelése"
        icon={BoltIcon}
        onRefresh={loadPods}
      />

      {/* Compact Status and Quick Stats */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Left: Status + Alerts */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-gray-700">ÉLŐ {format(new Date(), 'HH:mm', { locale: hu })}</span>
            </div>
            
            {/* Alerts */}
            <div className="flex flex-wrap items-center gap-1.5">
              {(() => {
                const poorQualityCount = filteredPods.filter(pod => pod.data_quality?.data_completeness === 'poor').length;
                const noCrmCount = filteredPods.filter(pod => !pod.customer_info).length;
                const staleDataCount = filteredPods.filter(pod => {
                  const lastUpdate = pod.last_update?.toDate?.();
                  if (!lastUpdate) return true;
                  const hoursDiff = (new Date().getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);
                  return hoursDiff > 48;
                }).length;

                return (
                  <>
                    {poorQualityCount > 0 && (
                      <span className="inline-flex items-center space-x-0.5 bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-xs">
                        <ExclamationTriangleIcon className="w-3 h-3" />
                        <span>{poorQualityCount}</span>
                      </span>
                    )}
                    {noCrmCount > 5 && (
                      <span className="inline-flex items-center space-x-0.5 bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-xs">
                        <ExclamationTriangleIcon className="w-3 h-3" />
                        <span>{noCrmCount} CRM</span>
                      </span>
                    )}
                    {staleDataCount > 0 && (
                      <span className="inline-flex items-center space-x-0.5 bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded text-xs">
                        <ClockIcon className="w-3 h-3" />
                        <span>{staleDataCount}</span>
                      </span>
                    )}
                    {poorQualityCount === 0 && noCrmCount <= 5 && staleDataCount === 0 && (
                      <span className="inline-flex items-center space-x-0.5 bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">
                        <CheckCircleIcon className="w-3 h-3" />
                        <span>OK</span>
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Right: Quick Stats */}
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span className="font-medium">{filteredPods.length} POD</span>
            <span>•</span>
            <span>{uniqueProviders.length} szolgáltató</span>
            <span>•</span>
            <span>{(totalConsumption / 1000000).toFixed(1)}M kWh</span>
          </div>
        </div>
      </div>

      {/* Enhanced Stats Cards with more information */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2 lg:gap-3">
        <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-2 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-1.5 rounded-md bg-blue-100 lg:hidden">
                <BuildingOffice2Icon className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 lg:mb-1">
                <p className="text-gray-600 text-xs font-medium">Összes POD</p>
                <p className="text-gray-800 text-base lg:text-sm font-bold mt-0.5">
                  {filteredPods.length}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {uniqueProviders.length} szolgáltató
                </p>
              </div>
            </div>
            <div className="hidden lg:block p-1 rounded-md bg-blue-100 lg:ml-1">
              <BuildingOffice2Icon className="w-4 h-4 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-2 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-1.5 rounded-md bg-purple-100 lg:hidden">
                <UsersIcon className="w-4 h-4 text-purple-600" />
              </div>
              <div className="flex-1 lg:mb-1">
                <p className="text-gray-600 text-xs font-medium">CRM kapcsolat</p>
                <p className="text-gray-800 text-base lg:text-sm font-bold mt-0.5">
                  {filteredPods.filter(pod => pod.customer_info).length}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {((filteredPods.filter(pod => pod.customer_info).length / filteredPods.length) * 100).toFixed(1)}% lefedettség
                </p>
              </div>
            </div>
            <div className="hidden lg:block p-1 rounded-md bg-purple-100 lg:ml-1">
              <UsersIcon className="w-4 h-4 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-2 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-1.5 rounded-md bg-green-100 lg:hidden">
                <BoltIcon className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 lg:mb-1">
                <p className="text-gray-600 text-xs font-medium">Fogyasztás</p>
                <p className="text-gray-800 text-sm lg:text-xs font-bold mt-0.5">
                  {(totalConsumption / 1000000).toFixed(1)}M kWh
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {(totalConsumption / filteredPods.length / 1000).toFixed(1)}k átlag/POD
                </p>
              </div>
            </div>
            <div className="hidden lg:block p-1 rounded-md bg-green-100 lg:ml-1">
              <BoltIcon className="w-4 h-4 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-2 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-1.5 rounded-md bg-yellow-100 lg:hidden">
                <CurrencyDollarIcon className="w-4 h-4 text-yellow-600" />
              </div>
              <div className="flex-1 lg:mb-1">
                <p className="text-gray-600 text-xs font-medium">Költség</p>
                <p className="text-gray-800 text-sm lg:text-xs font-bold mt-0.5">
                  {(totalCost / 1000000000).toFixed(1)}Mrd Ft
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {totalConsumption > 0 ? (totalCost / totalConsumption).toFixed(1) + ' Ft/kWh' : 'N/A'} átlag
                </p>
              </div>
            </div>
            <div className="hidden lg:block p-1 rounded-md bg-yellow-100 lg:ml-1">
              <CurrencyDollarIcon className="w-4 h-4 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-2 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-1.5 rounded-md bg-red-100 lg:hidden">
                <ChartBarIcon className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1 lg:mb-1">
                <p className="text-gray-600 text-xs font-medium">Mérések száma</p>
                <p className="text-gray-800 text-sm lg:text-xs font-bold mt-0.5">
                  {(filteredPods.reduce((sum, pod) => sum + (pod.measurement_count || 0), 0) / 1000).toFixed(0)}k
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {filteredPods.length > 0 ? (filteredPods.reduce((sum, pod) => sum + (pod.measurement_count || 0), 0) / filteredPods.length).toFixed(0) : '0'} átlag/POD
                </p>
              </div>
            </div>
            <div className="hidden lg:block p-1 rounded-md bg-red-100 lg:ml-1">
              <ChartBarIcon className="w-4 h-4 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-2 lg:space-x-0 lg:flex-col lg:items-start lg:flex-1 lg:min-w-0">
              <div className="p-1.5 rounded-md bg-indigo-100 lg:hidden">
                <ClockIcon className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="flex-1 lg:mb-1">
                <p className="text-gray-600 text-xs font-medium">Legutóbbi frissítés</p>
                <p className="text-gray-800 text-sm lg:text-xs font-bold mt-0.5">
                  {(() => {
                    const latestUpdate = filteredPods.reduce((latest, pod) => {
                      const podTime = pod.last_update?.toDate?.() || new Date(0);
                      return podTime > latest ? podTime : latest;
                    }, new Date(0));
                    return latestUpdate.getTime() > 0 ? format(latestUpdate, 'MM.dd', { locale: hu }) : 'N/A';
                  })()}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {(() => {
                    const now = new Date();
                    const latestUpdate = filteredPods.reduce((latest, pod) => {
                      const podTime = pod.last_update?.toDate?.() || new Date(0);
                      return podTime > latest ? podTime : latest;
                    }, new Date(0));
                    const hoursDiff = Math.floor((now.getTime() - latestUpdate.getTime()) / (1000 * 60 * 60));
                    return hoursDiff < 24 ? `${hoursDiff}h ezelőtt` : `${Math.floor(hoursDiff / 24)}d ezelőtt`;
                  })()}
                </p>
              </div>
            </div>
            <div className="hidden lg:block p-1 rounded-md bg-indigo-100 lg:ml-1">
              <ClockIcon className="w-4 h-4 text-indigo-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Compact Analytics Section with Toggles */}
      <div className="bg-white rounded-xl p-4 xl:p-6 shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base xl:text-lg font-semibold text-gray-800">Részletes analitika</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowProviderAnalytics(!showProviderAnalytics)}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showProviderAnalytics ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <ChartPieIcon className="w-4 h-4 mr-1" />
              Szolgáltatók
            </button>
            <button
              onClick={() => setShowConsumptionAnalytics(!showConsumptionAnalytics)}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showConsumptionAnalytics ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <BoltIcon className="w-4 h-4 mr-1" />
              Kategóriák
            </button>
            <button
              onClick={() => setShowQualityAnalytics(!showQualityAnalytics)}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showQualityAnalytics ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <SignalIcon className="w-4 h-4 mr-1" />
              Adatminőség
            </button>
          </div>
        </div>

        {/* Quick Summary when collapsed */}
        {!showProviderAnalytics && !showConsumptionAnalytics && !showQualityAnalytics && (
          <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">{uniqueProviders.length}</div>
              <div className="text-xs text-gray-600">szolgáltató aktív</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">
                {filteredPods.filter(pod => pod.total_consumption_kwh > 100000).length}
              </div>
              <div className="text-xs text-gray-600">nagy fogyasztó</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600">
                {Math.round((filteredPods.filter(pod => pod.data_quality?.data_completeness === 'good').length / filteredPods.length) * 100)}%
              </div>
              <div className="text-xs text-gray-600">jó adatminőség</div>
            </div>
          </div>
        )}

        {/* Expandable Analytics Sections */}
        <div className="space-y-4">
          {/* Provider Distribution */}
          {showProviderAnalytics && (
            <div className="border rounded-lg p-4 bg-blue-50/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-800">Szolgáltatói megoszlás</h4>
                <button
                  onClick={() => setShowProviderAnalytics(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {uniqueProviders.map(provider => {
                  const providerPods = filteredPods.filter(pod => pod.provider === provider);
                  const percentage = (providerPods.length / filteredPods.length) * 100;
                  const providerConsumption = providerPods.reduce((sum, pod) => sum + (pod.total_consumption_kwh || 0), 0);
                  const providerCost = providerPods.reduce((sum, pod) => sum + (pod.total_cost_huf || 0), 0);
                  
                  return (
                    <div key={provider} className="bg-white p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProviderColor(provider)}`}>
                          {provider.toUpperCase()}
                        </span>
                        <span className="text-sm font-medium text-gray-800">{percentage.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                        <div 
                          className={`h-2 rounded-full ${getProviderColor(provider).replace('text-', 'bg-').replace('100', '500')}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-xs">
                        <div className="text-center">
                          <div className="text-gray-500">POD</div>
                          <div className="font-medium">{providerPods.length}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500">kWh</div>
                          <div className="font-medium">{(providerConsumption / 1000).toFixed(0)}k</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500">Ft</div>
                          <div className="font-medium">{(providerCost / 1000000).toFixed(1)}M</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Consumption Categories */}
          {showConsumptionAnalytics && (
            <div className="border rounded-lg p-4 bg-green-50/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-800">Fogyasztási kategóriák</h4>
                <button
                  onClick={() => setShowConsumptionAnalytics(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Nagy fogyasztók', filter: (pod: EnergiaPOD) => pod.total_consumption_kwh > 100000, color: 'bg-red-500', icon: '🔥' },
                  { label: 'Közepes fogyasztók', filter: (pod: EnergiaPOD) => pod.total_consumption_kwh >= 10000 && pod.total_consumption_kwh <= 100000, color: 'bg-yellow-500', icon: '📊' },
                  { label: 'Kis fogyasztók', filter: (pod: EnergiaPOD) => pod.total_consumption_kwh > 0 && pod.total_consumption_kwh < 10000, color: 'bg-blue-500', icon: '📈' },
                  { label: 'Nulla fogyasztás', filter: (pod: EnergiaPOD) => pod.total_consumption_kwh === 0, color: 'bg-gray-500', icon: '⭕' }
                ].map(category => {
                  const categoryPods = filteredPods.filter(category.filter);
                  const percentage = (categoryPods.length / filteredPods.length) * 100;
                  const categoryConsumption = categoryPods.reduce((sum, pod) => sum + (pod.total_consumption_kwh || 0), 0);
                  
                  return (
                    <div key={category.label} className="bg-white p-3 rounded-lg text-center">
                      <div className="text-lg mb-1">{category.icon}</div>
                      <div className="text-sm font-medium text-gray-800 mb-1">{category.label}</div>
                      <div className="text-lg font-bold text-gray-700">{categoryPods.length}</div>
                      <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div 
                          className={`h-1.5 rounded-full ${category.color}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {(categoryConsumption / 1000).toFixed(0)}k kWh
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Data Quality */}
          {showQualityAnalytics && (
            <div className="border rounded-lg p-4 bg-purple-50/30">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-800">Adatminőség áttekintő</h4>
                <button
                  onClick={() => setShowQualityAnalytics(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Kiváló adatminőség', value: 'good', color: 'bg-green-500', icon: '✅' },
                  { label: 'Korlátozott adatok', value: 'limited', color: 'bg-yellow-500', icon: '⚠️' },
                  { label: 'Gyenge adatminőség', value: 'poor', color: 'bg-red-500', icon: '❌' },
                  { label: 'Nincs adat', value: null, color: 'bg-gray-500', icon: '❓' }
                ].map(quality => {
                  const qualityPods = filteredPods.filter(pod => 
                    quality.value === null 
                      ? !pod.data_quality?.data_completeness 
                      : pod.data_quality?.data_completeness === quality.value
                  );
                  const percentage = (qualityPods.length / filteredPods.length) * 100;
                  const qualityMeasurements = qualityPods.reduce((sum, pod) => sum + (pod.measurement_count || 0), 0);
                  
                  return (
                    <div key={quality.label} className="bg-white p-3 rounded-lg text-center">
                      <div className="text-lg mb-1">{quality.icon}</div>
                      <div className="text-sm font-medium text-gray-800 mb-1">{quality.label}</div>
                      <div className="text-lg font-bold text-gray-700">{qualityPods.length}</div>
                      <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div 
                          className={`h-1.5 rounded-full ${quality.color}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {(qualityMeasurements / 1000).toFixed(0)}k mérés
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>



      {/* Filters */}
      <div className="bg-white rounded-xl p-4 xl:p-6 shadow-md">
        <div className="flex items-center space-x-2 mb-4">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <span className="text-base xl:text-lg font-medium text-gray-700">Szűrők és keresés</span>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Keresés POD ID, szolgáltató vagy ügyfél szerint..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm placeholder:text-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Compact Filter Layout - 2 rows */}
        <div className="space-y-3">
          {/* First Row: Provider & CRM Connection */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Provider Filters */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                🏢 Szolgáltató ({uniqueProviders.length})
              </label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setSelectedProvider('')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    !selectedProvider
                      ? 'bg-primary-100 text-primary-800 border border-primary-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Mind ({pods.length})
                </button>
                {uniqueProviders.map(provider => (
                  <button
                    key={provider}
                    onClick={() => setSelectedProvider(provider)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                      selectedProvider === provider
                        ? 'bg-primary-100 text-primary-800 border border-primary-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {provider.toUpperCase()} ({pods.filter(p => p.provider === provider).length})
                  </button>
                ))}
              </div>
            </div>

            {/* CRM Connection Filters */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                👥 CRM kapcsolat
              </label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setSelectedConnectionStatus('')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    !selectedConnectionStatus
                      ? 'bg-primary-100 text-primary-800 border border-primary-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Mind ({pods.length})
                </button>
                <button
                  onClick={() => setSelectedConnectionStatus('connected')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    selectedConnectionStatus === 'connected'
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ✅ Van ({pods.filter(p => p.customer_info && p.customer_info.customer_name !== 'Nincs hozzárendelve').length})
                </button>
                <button
                  onClick={() => setSelectedConnectionStatus('unconnected')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    selectedConnectionStatus === 'unconnected'
                      ? 'bg-orange-100 text-orange-800 border border-orange-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ⚠️ Nincs ({pods.filter(p => !p.customer_info || p.customer_info.customer_name === 'Nincs hozzárendelve').length})
                </button>
              </div>
            </div>
          </div>

          {/* Second Row: Month Selector & Data Quality */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Month Selector */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                📅 Hónap szűrő
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Összes hónap ({pods.length} POD)</option>
                {availableMonths.map(month => {
                  const [year, monthNum] = month.split('-');
                  const monthNames = ['Jan', 'Feb', 'Már', 'Ápr', 'Máj', 'Jún', 'Júl', 'Aug', 'Szep', 'Okt', 'Nov', 'Dec'];
                  const monthName = monthNames[parseInt(monthNum) - 1];
                  const podCount = pods.filter(p => {
                    const start = p.date_range?.start?.toDate ? p.date_range.start.toDate() : null;
                    const end = p.date_range?.end?.toDate ? p.date_range.end.toDate() : (p.last_update?.toDate ? p.last_update.toDate() : null);
                    if (!start || !end) return false;
                    const startMonth = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
                    const endMonth = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}`;
                    return month >= startMonth && month <= endMonth;
                  }).length;
                  return (
                    <option key={month} value={month}>
                      {year}. {monthName} ({podCount} POD)
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Data Quality Filters */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                📊 Adatminőség
              </label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setSelectedDataQuality('')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    !selectedDataQuality
                      ? 'bg-primary-100 text-primary-800 border border-primary-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Mind ({pods.length})
                </button>
                <button
                  onClick={() => setSelectedDataQuality('good')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    selectedDataQuality === 'good'
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ✅ Jó ({pods.filter(p => p.data_quality?.data_completeness === 'good').length})
                </button>
                <button
                  onClick={() => setSelectedDataQuality('limited')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    selectedDataQuality === 'limited'
                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ⚠️ Korlátozott ({pods.filter(p => p.data_quality?.data_completeness === 'limited').length})
                </button>
                <button
                  onClick={() => setSelectedDataQuality('poor')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    selectedDataQuality === 'poor'
                      ? 'bg-red-100 text-red-800 border border-red-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ❌ Gyenge ({pods.filter(p => p.data_quality?.data_completeness === 'poor').length})
                </button>
              </div>
            </div>

            {/* Consumption Level Filters */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                ⚡ Fogyasztás
              </label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setConsumptionFilter('')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    !consumptionFilter
                      ? 'bg-primary-100 text-primary-800 border border-primary-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Mind ({pods.length})
                </button>
                <button
                  onClick={() => setConsumptionFilter('high')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    consumptionFilter === 'high'
                      ? 'bg-red-100 text-red-800 border border-red-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  🔥 {'>'} 100k ({pods.filter(p => p.total_consumption_kwh > 100000).length})
                </button>
                <button
                  onClick={() => setConsumptionFilter('medium')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    consumptionFilter === 'medium'
                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📊 10-100k ({pods.filter(p => p.total_consumption_kwh >= 10000 && p.total_consumption_kwh <= 100000).length})
                </button>
                <button
                  onClick={() => setConsumptionFilter('low')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    consumptionFilter === 'low'
                      ? 'bg-blue-100 text-blue-800 border border-blue-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  📈 {'<'} 10k ({pods.filter(p => p.total_consumption_kwh > 0 && p.total_consumption_kwh < 10000).length})
                </button>
                <button
                  onClick={() => setConsumptionFilter('zero')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    consumptionFilter === 'zero'
                      ? 'bg-gray-100 text-gray-800 border border-gray-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ⭕ Nulla ({pods.filter(p => p.total_consumption_kwh === 0).length})
                </button>
                <button
                  onClick={() => setConsumptionFilter('reverse_feed')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    consumptionFilter === 'reverse_feed'
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ☀️ Visszatáplálással ({pods.filter(p => p.total_reverse_feed_kwh && p.total_reverse_feed_kwh > 0).length})
                </button>
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          <div className="flex justify-end">
            <button
              onClick={() => {
                setSelectedProvider('');
                setSelectedConnectionStatus('');
                setSelectedDataQuality('');
                setConsumptionFilter('');
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium"
            >
              🗑️ Szűrők törlése
            </button>
          </div>
        </div>
      </div>

      {/* PODs Table - Mobile Responsive */}
      <div className="bg-white rounded-xl shadow-md">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-hidden rounded-xl">
          {/* Scroll hint */}
          <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-500 flex items-center justify-between">
            <span>📊 POD lista - {currentPagePods.length} elem (összes: {filteredPods.length}) - Oldal {currentPage}/{totalPages}</span>
          </div>
          <div 
            className="overflow-x-scroll overflow-y-visible"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'auto',
              scrollbarColor: '#3B82F6 #E5E7EB',
              overscrollBehaviorX: 'contain'
            }}
          >
            <table className="w-full divide-y divide-gray-200" style={{ minWidth: '1300px' }}>
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('pod_id')}
                    style={{ minWidth: '200px' }}
                  >
                    POD ID {getSortIcon('pod_id')}
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('customer_name')}
                  >
                    Ügyfél {getSortIcon('customer_name')}
                  </th>
                  <th 
                    className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('provider')}
                    style={{ minWidth: '90px' }}
                  >
                    Szolgáltató {getSortIcon('provider')}
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('consumption')}
                    style={{ minWidth: '140px' }}
                  >
                    FOGYASZTÁS [kWh] {getSortIcon('consumption')}
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('reverse_feed')}
                    style={{ minWidth: '160px' }}
                  >
                    VISSZATÁPLÁLÁS [kWh] {getSortIcon('reverse_feed')}
                  </th>
                  <th 
                    className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('measurements')}
                    style={{ minWidth: '80px' }}
                  >
                    Mérések {getSortIcon('measurements')}
                  </th>
                  <th 
                    className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('last_update')}
                    style={{ minWidth: '80px' }}
                  >
                    Frissítve {getSortIcon('last_update')}
                  </th>
                  {/* Extra oszlopok csak nagyobb képernyőkön */}
                  <th className="hidden 2xl:table-cell px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '100px' }}>
                    CRM
                  </th>
                  <th className="hidden 2xl:table-cell px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '100px' }}>
                    AM
                  </th>
                  {/* <th className="hidden 2xl:table-cell px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '80px' }}>
                    Trend
                  </th> */}
                  {/* Műveletek oszlop a végén */}
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-lg border-l border-gray-200"
                    style={{ minWidth: '80px' }}
                  >
                    Opciók
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={12} className="px-6 py-4 text-center text-gray-500">
                      <div className="flex items-center justify-center">
                        <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                        Betöltés...
                      </div>
                    </td>
                  </tr>
                ) : filteredPods.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-6 py-4 text-center text-gray-500">
                      Nincsenek POD-ok a megadott szűrőkkel
                    </td>
                  </tr>
                ) : (
                  currentPagePods.map((pod) => (
                    <tr 
                      key={pod.id} 
                      className="hover:bg-blue-50 hover:shadow-sm cursor-pointer transition-all duration-200"
                      onClick={() => handleViewDetails(pod)}
                      onMouseEnter={() => handlePodHover(pod.id)}
                      title={getLastMonthTooltip(pod)}
                    >
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="text-xs font-medium text-gray-900 font-mono break-all max-w-[180px]">
                            {pod.pod_id}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(pod.pod_id);
                              setCopiedPodId(pod.pod_id);
                              setTimeout(() => setCopiedPodId(null), 2000);
                            }}
                            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors relative"
                            title={copiedPodId === pod.pod_id ? "Másolva!" : "POD ID másolása"}
                          >
                            {copiedPodId === pod.pod_id ? (
                              <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-3.5 h-3.5 text-gray-500 hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {pod.customer_info ? (
                          <div className="flex flex-col">
                            <div className="text-xs font-medium text-gray-900 truncate max-w-[180px]" title={pod.customer_info.customer_name}>
                              {pod.customer_info.customer_name}
                            </div>
                            {pod.customer_info.customer_type && pod.customer_info.customer_name !== 'Nincs hozzárendelve' && (
                              <span className={`inline-flex px-1 py-0.5 text-xs font-semibold rounded-full w-fit mt-1 ${getCustomerTypeColor(pod.customer_info.customer_type)}`}>
                                {pod.customer_info.customer_type === 'organization' ? '🏢' : '👤'}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Nincs adat</span>
                        )}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getProviderColor(pod.provider)}`}>
                          {pod.provider.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900 font-semibold">
                        {(pod.total_consumption_kwh / 1000).toFixed(1)}k
                      </td>
                      <td className={`px-3 py-3 whitespace-nowrap text-xs font-semibold ${pod.total_reverse_feed_kwh && pod.total_reverse_feed_kwh > 0 ? 'bg-green-50' : ''}`}>
                        {pod.total_reverse_feed_kwh && pod.total_reverse_feed_kwh > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-green-700">
                              {pod.total_reverse_feed_kwh >= 100 
                                ? `${(pod.total_reverse_feed_kwh / 1000).toFixed(1)}k`
                                : `${pod.total_reverse_feed_kwh.toFixed(2)} kWh`
                              }
                            </span>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" title="Napelem / Visszatáplálás">
                              ☀️
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900">
                        {((pod.measurement_count ?? 0) / 1000).toFixed(1)}k
                      </td>
                      <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900">
                        <div className="flex flex-col">
                          <span>
                            {pod.last_update?.toDate ? 
                              format(pod.last_update.toDate(), 'MM.dd', { locale: hu }) :
                              'N/A'
                            }
                          </span>
                          <span className="text-xs text-gray-400">
                            {pod.last_update?.toDate ? 
                              format(pod.last_update.toDate(), 'HH:mm', { locale: hu }) :
                              ''
                            }
                          </span>
                        </div>
                      </td>
                      {/* Extra oszlopok csak nagyobb képernyőkön */}
                      <td className="hidden 2xl:table-cell px-2 py-3 whitespace-nowrap">
                        {pod.customer_info && pod.customer_info.customer_name !== 'Nincs hozzárendelve' ? (
                          <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        ) : (
                          <ExclamationTriangleIcon className="w-4 h-4 text-orange-500" />
                        )}
                      </td>
                      <td className="hidden 2xl:table-cell px-2 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          {getDataQualityIcon(pod.data_quality?.data_completeness)}
                        </div>
                      </td>
                      {/* <td className="hidden 2xl:table-cell px-2 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          {getTrendIcon(pod.trends?.trend_direction)}
                          <span className="text-xs text-gray-600 ml-1">
                            {pod.trends?.trend_percentage ? 
                              `${pod.trends.trend_percentage > 0 ? '+' : ''}${pod.trends.trend_percentage.toFixed(0)}%` :
                              ''
                            }
                          </span>
                        </div>
                      </td> */}
                      {/* Műveletek oszlop a végén */}
                      <td className="px-3 py-3 whitespace-nowrap text-xs font-medium sticky right-0 bg-white shadow-lg border-l border-gray-200">
                        <div className="flex items-center justify-center text-primary-600">
                          <EyeIcon className="w-4 h-4" />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {/* Aggregált összesítő sor */}
              {!loading && filteredPods.length > 0 && (
                <tfoot className="bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-blue-200">
                  <tr className="font-semibold">
                    <td className="px-3 py-3 text-xs text-gray-700" colSpan={3}>
                      📊 ÖSSZESÍTŐ ({filteredPods.length} POD)
                    </td>
                    <td className="px-3 py-3 text-xs text-blue-900">
                      {energiaService.formatKWH(filteredPods.reduce((sum, pod) => sum + (pod.total_consumption_kwh || 0), 0))}
                    </td>
                    <td className="px-3 py-3 text-xs text-green-700">
                      {energiaService.formatKWH(filteredPods.reduce((sum, pod) => sum + (pod.total_reverse_feed_kwh || 0), 0))}
                    </td>
                    <td className="px-2 py-3 text-xs text-gray-600">
                      {filteredPods.reduce((sum, pod) => sum + (pod.measurement_count || 0), 0).toLocaleString('hu-HU')}
                    </td>
                    <td className="px-2 py-3 text-xs text-gray-500" colSpan={2}>
                      -
                    </td>
                    <td className="hidden 2xl:table-cell px-3 py-3 text-xs text-gray-700" colSpan={2}>
                      Költség: {energiaService.formatHUF(filteredPods.reduce((sum, pod) => sum + (pod.total_cost_huf || 0), 0))}
                    </td>
                    <td className="px-3 py-3 sticky right-0 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg border-l border-blue-200">
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Mobile Card Layout */}
        <div className="lg:hidden">
          {/* Mobile hint */}
          <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-500 text-center">
            📊 {currentPagePods.length} POD (összes: {filteredPods.length}) - Oldal {currentPage}/{totalPages} - Kattints egy kártyára a részletekért
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-600 mr-3" />
              <span className="text-gray-600">POD adatok betöltése...</span>
            </div>
          ) : filteredPods.length === 0 ? (
            <div className="text-center py-12 px-4">
              <BoltIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Nincsenek POD-ok a megadott szűrőkkel</p>
            </div>
          ) : (
            <div className="space-y-3 p-4">
              {currentPagePods.map((pod) => (
                <div 
                  key={pod.id} 
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md hover:border-primary-200 cursor-pointer transition-all"
                  onClick={() => handleViewDetails(pod)}
                  onMouseEnter={() => handlePodHover(pod.id)}
                  onTouchStart={() => handlePodHover(pod.id)}
                  title={getLastMonthTooltip(pod)}
                >
                  {/* Header sor - POD ID és Szolgáltató */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 font-mono truncate mb-1">
                        {pod.pod_id}
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProviderColor(pod.provider)}`}>
                        {pod.provider.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      {pod.customer_info && pod.customer_info.customer_name !== 'Nincs hozzárendelve' ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      ) : (
                        <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />
                      )}
                      <EyeIcon className="w-5 h-5 text-primary-600" />
                    </div>
                  </div>

                  {/* Ügyfél információ */}
                  {pod.customer_info && (
                    <div className="mb-3 p-2 bg-gray-50 rounded-md">
                      <div className="text-xs text-gray-500 mb-1">Ügyfél:</div>
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {pod.customer_info.customer_name}
                      </div>
                    </div>
                  )}
                  
                  {/* Fő adatok - 2 oszlop grid */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="bg-blue-50 p-2 rounded-md">
                      <div className="text-xs text-gray-500 mb-1">Fogyasztás</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {(pod.total_consumption_kwh / 1000).toFixed(0)}k kWh
                      </div>
                    </div>
                    <div className="bg-green-50 p-2 rounded-md">
                      <div className="text-xs text-gray-500 mb-1">Költség</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {(pod.total_cost_huf / 1000000).toFixed(1)}M Ft
                      </div>
                    </div>
                  </div>

                  {/* Alsó sor - Statisztikák */}
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-2">
                      {getDataQualityIcon(pod.data_quality?.data_completeness)}
                      <span className="text-gray-600">
                        {(pod.measurement_count ?? 0).toLocaleString('hu-HU')} mérés
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-500">
                      {getTrendIcon(pod.trends?.trend_direction)}
                      <span>
                        {pod.last_update?.toDate ? 
                          format(pod.last_update.toDate(), 'MM.dd', { locale: hu }) :
                          'N/A'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Mobil összesítő kártya */}
              {!loading && filteredPods.length > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4 shadow-md">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    📊 ÖSSZESÍTŐ ({filteredPods.length} POD)
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Fogyasztás</p>
                      <p className="text-sm font-bold text-blue-900">
                        {energiaService.formatKWH(filteredPods.reduce((sum, pod) => sum + (pod.total_consumption_kwh || 0), 0))}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Visszatáplálás</p>
                      <p className="text-sm font-bold text-green-700">
                        {energiaService.formatKWH(filteredPods.reduce((sum, pod) => sum + (pod.total_reverse_feed_kwh || 0), 0))}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Költség</p>
                      <p className="text-sm font-bold text-gray-900">
                        {energiaService.formatHUF(filteredPods.reduce((sum, pod) => sum + (pod.total_cost_huf || 0), 0))}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Mérések</p>
                      <p className="text-sm font-bold text-gray-600">
                        {filteredPods.reduce((sum, pod) => sum + (pod.measurement_count || 0), 0).toLocaleString('hu-HU')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mobil lapozás info */}
              <div className="text-center py-4 text-sm text-gray-500">
                Összesen {filteredPods.length} POD megjelenítve
              </div>
            </div>
          )}
        </div>

        {/* Pagination Component */}
        {filteredPods.length > 0 && (
          <div className="bg-white px-4 py-3 sm:px-6 border-t border-gray-200 rounded-b-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-700">
                  Megjelenítve: <span className="font-medium">{startIndex + 1}</span> - <span className="font-medium">{Math.min(endIndex, filteredPods.length)}</span> / <span className="font-medium">{filteredPods.length}</span> POD
                </p>
                <div className="flex items-center space-x-2">
                  <label htmlFor="itemsPerPage" className="text-sm text-gray-700">Laponként:</label>
                  <select
                    id="itemsPerPage"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  ←
                </button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                    let pageNumber;
                    if (totalPages <= 7) {
                      pageNumber = i + 1;
                    } else if (currentPage <= 4) {
                      pageNumber = i + 1;
                    } else if (currentPage >= totalPages - 3) {
                      pageNumber = totalPages - 6 + i;
                    } else {
                      pageNumber = currentPage - 3 + i;
                    }
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNumber
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>
                
                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* POD Details Modal */}
      {selectedPod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 xl:p-4 z-50">
          <div className="bg-white rounded-xl max-w-7xl w-full max-h-[95vh] xl:max-h-[90vh] overflow-y-auto">
            <div className="p-4 xl:p-6">
              <div className="flex items-center justify-between mb-4 xl:mb-6">
                <h2 className="text-lg xl:text-xl font-semibold text-gray-800">
                  <span className="hidden xl:inline">POD Részletek: </span>
                  <span className="font-mono text-sm xl:text-base">{selectedPod.pod_id}</span>
                </h2>
                <button
                  onClick={() => setSelectedPod(null)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <XMarkIcon className="w-5 h-5 xl:w-6 xl:h-6" />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className="border-b border-gray-200 mb-4 xl:mb-6">
                <nav className="-mb-px flex space-x-4 xl:space-x-8 overflow-x-auto">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs xl:text-sm whitespace-nowrap ${
                      activeTab === 'details'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <EyeIcon className="w-3 h-3 xl:w-4 xl:h-4 inline mr-1 xl:mr-2" />
                    Alapadatok
                  </button>
                  <button
                    onClick={() => setActiveTab('measurements')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs xl:text-sm whitespace-nowrap ${
                      activeTab === 'measurements'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <ChartPieIcon className="w-3 h-3 xl:w-4 xl:h-4 inline mr-1 xl:mr-2" />
                    Mérési adatok ({measurementData.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('daily')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs xl:text-sm whitespace-nowrap ${
                      activeTab === 'daily'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <TableCellsIcon className="w-3 h-3 xl:w-4 xl:h-4 inline mr-1 xl:mr-2" />
                    Napi összesítők ({dailySummaries.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('monthly')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs xl:text-sm whitespace-nowrap ${
                      activeTab === 'monthly'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <CalendarDaysIcon className="w-3 h-3 xl:w-4 xl:h-4 inline mr-1 xl:mr-2" />
                    Havi összesítők ({monthlySummaries.length})
                  </button>
                  {selectedPod.total_reverse_feed_kwh && selectedPod.total_reverse_feed_kwh > 0 && (
                    <button
                      onClick={() => setActiveTab('reverse_feed')}
                      className={`py-2 px-1 border-b-2 font-medium text-xs xl:text-sm whitespace-nowrap ${
                        activeTab === 'reverse_feed'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="inline-flex items-center">
                        <span className="mr-1 xl:mr-2">☀️</span>
                        Visszatáplálási adatok
                      </span>
                    </button>
                  )}
                  
                  {/* MSCONS Tab */}
                  <button
                    onClick={() => setActiveTab('mscons')}
                    className={`py-2 px-1 border-b-2 font-medium text-xs xl:text-sm whitespace-nowrap ${
                      activeTab === 'mscons'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="inline-flex items-center">
                      <span className="mr-1 xl:mr-2">📊</span>
                      MSCONS Számlázás
                    </span>
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              {activeTab === 'details' && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-8">
                  {/* POD Alapadatok */}
                  <div className="space-y-4 xl:space-y-6">
                    <div className="bg-gray-50 rounded-lg p-3 xl:p-4">
                      <h3 className="text-base xl:text-lg font-medium text-gray-800 mb-3 xl:mb-4">POD Alapadatok</h3>
                      <div className="space-y-3 xl:space-y-4">
                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">POD ID</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900 font-mono break-all">{selectedPod.pod_id}</p>
                        </div>

                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Szolgáltató</label>
                          <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getProviderColor(selectedPod.provider)}`}>
                            {selectedPod.provider.toUpperCase()}
                          </span>
                        </div>

                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Összes fogyasztás</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900">
                            {energiaService.formatKWH(selectedPod.total_consumption_kwh)}
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Összes költség</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900">
                            {energiaService.formatHUF(selectedPod.total_cost_huf)}
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Mérések száma</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900">
                            {(selectedPod.measurement_count ?? 0).toLocaleString('hu-HU')}
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Átlagos ár</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900">
                            {selectedPod.total_consumption_kwh > 0 ? 
                              energiaService.formatHUF(selectedPod.total_cost_huf / selectedPod.total_consumption_kwh) + ' / kWh' :
                              'N/A'
                            }
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Utolsó frissítés</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900">
                            {selectedPod.last_update?.toDate ? 
                              format(selectedPod.last_update.toDate(), 'yyyy.MM.dd HH:mm:ss', { locale: hu }) :
                              'N/A'
                            }
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs xl:text-sm font-medium text-gray-700">Időszak</label>
                          <p className="mt-1 text-xs xl:text-sm text-gray-900">
                            {selectedPod.date_range?.start?.toDate && selectedPod.date_range?.end?.toDate ? 
                              `${format(selectedPod.date_range.start.toDate(), 'yyyy.MM.dd', { locale: hu })} - ${format(selectedPod.date_range.end.toDate(), 'yyyy.MM.dd', { locale: hu })}` :
                              'N/A'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ügyfél Adatok */}
                  <div className="space-y-4 xl:space-y-6">
                    {selectedPod.customer_info ? (
                      <div className="bg-blue-50 rounded-lg p-3 xl:p-4">
                        <h3 className="text-base xl:text-lg font-medium text-gray-800 mb-3 xl:mb-4">Ügyfél Adatok</h3>
                        <div className="space-y-3 xl:space-y-4">
                          <div>
                            <label className="block text-xs xl:text-sm font-medium text-gray-700">Ügyfél neve</label>
                            <p className="mt-1 text-xs xl:text-sm text-gray-900">{selectedPod.customer_info.customer_name}</p>
                          </div>

                          {selectedPod.customer_info.company_name && (
                            <div>
                              <label className="block text-xs xl:text-sm font-medium text-gray-700">Cégnév</label>
                              <p className="mt-1 text-xs xl:text-sm text-gray-900">{selectedPod.customer_info.company_name}</p>
                            </div>
                          )}

                          <div>
                            <label className="block text-xs xl:text-sm font-medium text-gray-700">Ügyfél típus</label>
                            <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCustomerTypeColor(selectedPod.customer_info.customer_type)}`}>
                              {selectedPod.customer_info.customer_type === 'organization' ? 'Szervezet' : 'Magánszemély'}
                            </span>
                          </div>

                          {selectedPod.customer_info.contact_email && (
                            <div>
                              <label className="block text-xs xl:text-sm font-medium text-gray-700">Email</label>
                              <p className="mt-1 text-xs xl:text-sm text-gray-900 break-all">{selectedPod.customer_info.contact_email}</p>
                            </div>
                          )}

                          {selectedPod.customer_info.contact_phone && (
                            <div>
                              <label className="block text-xs xl:text-sm font-medium text-gray-700">Telefon</label>
                              <p className="mt-1 text-xs xl:text-sm text-gray-900">{selectedPod.customer_info.contact_phone}</p>
                            </div>
                          )}

                          {selectedPod.customer_info.address && (
                            <div>
                              <label className="block text-xs xl:text-sm font-medium text-gray-700">Cím</label>
                              <p className="mt-1 text-xs xl:text-sm text-gray-900">{selectedPod.customer_info.address}</p>
                            </div>
                          )}

                          {selectedPod.customer_info.tax_number && (
                            <div>
                              <label className="block text-xs xl:text-sm font-medium text-gray-700">Adószám</label>
                              <p className="mt-1 text-xs xl:text-sm text-gray-900">{selectedPod.customer_info.tax_number}</p>
                            </div>
                          )}

                          <div>
                            <label className="block text-xs xl:text-sm font-medium text-gray-700">Ügyfél státusz</label>
                            <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              selectedPod.customer_info.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {selectedPod.customer_info.is_active ? 'Aktív' : 'Inaktív'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 rounded-lg p-3 xl:p-4">
                        <h3 className="text-base xl:text-lg font-medium text-gray-800 mb-3 xl:mb-4">Ügyfél Adatok</h3>
                        <div className="text-center py-6 xl:py-8">
                          <UsersIcon className="w-8 h-8 xl:w-12 xl:h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-xs xl:text-sm text-gray-600">
                            Ehhez a POD-hoz nem található CRM ügyfél adat
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            A POD ID nem egyezik meg egyik CRM ügyfél POD-jával sem
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Analytics és Statisztikák */}
                    {(selectedPod.statistics || selectedPod.peak_analysis || selectedPod.data_quality) && (
                      <div className="bg-green-50 rounded-lg p-3 xl:p-4">
                        <h3 className="text-base xl:text-lg font-medium text-gray-800 mb-3 xl:mb-4">Analytics</h3>
                        <div className="space-y-3">
                          {selectedPod.statistics && (
                            <>
                              <div>
                                <label className="block text-xs xl:text-sm font-medium text-gray-700">Napi átlag fogyasztás</label>
                                <p className="mt-1 text-xs xl:text-sm text-gray-900">
                                  {energiaService.formatKWH(selectedPod.statistics.avg_daily_consumption)}
                                </p>
                              </div>
                              <div>
                                <label className="block text-xs xl:text-sm font-medium text-gray-700">Max napi fogyasztás</label>
                                <p className="mt-1 text-xs xl:text-sm text-gray-900">
                                  {energiaService.formatKWH(selectedPod.statistics.max_daily_consumption)}
                                </p>
                              </div>
                            </>
                          )}

                          {selectedPod.peak_analysis && (
                            <div>
                              <label className="block text-xs xl:text-sm font-medium text-gray-700">Csúcsóra</label>
                              <p className="mt-1 text-xs xl:text-sm text-gray-900">
                                {selectedPod.peak_analysis.peak_hour}:00
                              </p>
                            </div>
                          )}

                          {selectedPod.data_quality && (
                            <div>
                              <label className="block text-xs xl:text-sm font-medium text-gray-700">Adatminőség</label>
                              <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDataQualityColor(selectedPod.data_quality.data_completeness)}`}>
                                {getDataQualityIcon(selectedPod.data_quality.data_completeness)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Measurements Tab */}
              {activeTab === 'measurements' && (
                <div className="space-y-4">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 xl:gap-0">
                    <h3 className="text-base xl:text-lg font-medium text-gray-800">
                      Mérési adatok (legutóbbi {measurementLimit === 99999 ? 'összes' : measurementLimit})
                    </h3>
                    <div className="flex items-center gap-3">
                      <label className="text-xs xl:text-sm font-medium text-gray-600">
                        Adatok száma:
                      </label>
                      <select
                        value={measurementLimit}
                        onChange={(e) => {
                          const newLimit = parseInt(e.target.value);
                          setMeasurementLimit(newLimit);
                          if (selectedPod) {
                            loadMeasurementData(selectedPod.id, newLimit);
                          }
                        }}
                        className="px-2 xl:px-3 py-1 border border-gray-300 rounded-md text-xs xl:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={100}>100</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                        <option value={99999}>Összes (~2800)</option>
                      </select>
                      {loadingMeasurements && (
                        <ArrowPathIcon className="w-4 h-4 animate-spin text-blue-500" />
                      )}
                    </div>
                  </div>

                  {loadingMeasurements ? (
                    <div className="text-center py-12">
                      <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
                      <p className="mt-2 text-sm xl:text-base text-gray-600">Mérési adatok betöltése...</p>
                    </div>
                  ) : measurementData.length === 0 ? (
                    <div className="text-center py-12">
                      <ChartPieIcon className="w-8 h-8 xl:w-12 xl:h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm xl:text-base text-gray-600">Nincsenek elérhető mérési adatok</p>
                    </div>
                  ) : (
                    <div className="bg-white border rounded-lg overflow-hidden">
                      {/* Desktop Table */}
                      <div className="hidden xl:block overflow-x-auto max-h-96">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Időpont
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Fogyasztás
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ár
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Költség
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Metadata
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {measurementData.map((measurement, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {measurement.timestamp?.toDate ? 
                                    format(measurement.timestamp.toDate(), 'MM.dd HH:mm', { locale: hu }) :
                                    'N/A'
                                  }
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {energiaService.formatKWH(measurement.consumption)}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {energiaService.formatHUF(measurement.price_huf_kwh)} / kWh
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {energiaService.formatHUF(measurement.cost_huf)}
                                </td>
                                <td className="px-4 py-2 text-xs text-gray-500">
                                  {measurement.metadata && (
                                    <div className="space-y-1">
                                      <div>Óra: {measurement.metadata.hour}</div>
                                      <div className="flex space-x-2">
                                        {measurement.metadata.is_weekend && (
                                          <span className="bg-blue-100 text-blue-800 px-1 rounded">Hétvége</span>
                                        )}
                                        {measurement.metadata.is_peak_hour && (
                                          <span className="bg-red-100 text-red-800 px-1 rounded">Csúcs</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card Layout */}
                      <div className="xl:hidden max-h-96 overflow-y-auto">
                        <div className="divide-y divide-gray-200">
                          {measurementData.map((measurement, index) => (
                            <div key={index} className="p-3">
                              <div className="flex justify-between items-start mb-2">
                                <div className="text-sm font-medium text-gray-900">
                                  {measurement.timestamp?.toDate ? 
                                    format(measurement.timestamp.toDate(), 'MM.dd HH:mm', { locale: hu }) :
                                    'N/A'
                                  }
                                </div>
                                <div className="flex space-x-1">
                                  {measurement.metadata?.is_weekend && (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 text-xs rounded">Hétvége</span>
                                  )}
                                  {measurement.metadata?.is_peak_hour && (
                                    <span className="bg-red-100 text-red-800 px-2 py-1 text-xs rounded">Csúcs</span>
                                  )}
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-xs text-gray-500 block">Fogyasztás</span>
                                  <span className="text-gray-900">
                                    {energiaService.formatKWH(measurement.consumption)}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-500 block">Költség</span>
                                  <span className="text-gray-900">
                                    {energiaService.formatHUF(measurement.cost_huf)}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-gray-500">
                                Ár: {energiaService.formatHUF(measurement.price_huf_kwh)} / kWh
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Daily Summaries Tab */}
              {activeTab === 'daily' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base xl:text-lg font-medium text-gray-800">
                      Napi összesítők (legutóbbi 30 nap)
                    </h3>
                    {loadingMeasurements && (
                      <ArrowPathIcon className="w-5 h-5 animate-spin text-primary-600" />
                    )}
                  </div>

                  {loadingMeasurements ? (
                    <div className="text-center py-12">
                      <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
                      <p className="mt-2 text-sm xl:text-base text-gray-600">Napi összesítők betöltése...</p>
                    </div>
                  ) : dailySummaries.length === 0 ? (
                    <div className="text-center py-12">
                      <TableCellsIcon className="w-8 h-8 xl:w-12 xl:h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm xl:text-base text-gray-600">Nincsenek elérhető napi összesítők</p>
                    </div>
                  ) : (
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-96">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Dátum
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Napi fogyasztás
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Napi költség
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Átlagár
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Feltöltve
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {dailySummaries.map((summary, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-3 xl:px-4 py-2 text-xs xl:text-sm font-medium text-gray-900">
                                  {summary.date?.toDate ? 
                                    format(summary.date.toDate(), 'yyyy.MM.dd', { locale: hu }) :
                                    'N/A'
                                  }
                                </td>
                                <td className="px-3 xl:px-4 py-2 text-xs xl:text-sm text-gray-900">
                                  {energiaService.formatKWH(summary.total_consumption_kwh)}
                                </td>
                                <td className="px-3 xl:px-4 py-2 text-xs xl:text-sm text-gray-900">
                                  {energiaService.formatHUF(summary.total_cost_huf)}
                                </td>
                                <td className="px-3 xl:px-4 py-2 text-xs xl:text-sm text-gray-900">
                                  {energiaService.formatHUF(summary.avg_price_huf_kwh)} / kWh
                                </td>
                                <td className="px-3 xl:px-4 py-2 text-xs xl:text-sm text-gray-500">
                                  {summary.upload_date?.toDate ? 
                                    format(summary.upload_date.toDate(), 'MM.dd HH:mm', { locale: hu }) :
                                    'N/A'
                                  }
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Monthly Summaries Tab */}
              {activeTab === 'monthly' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base xl:text-lg font-medium text-gray-800">
                      Havi összesítők (legutóbbi 12 hónap)
                    </h3>
                    {loadingMeasurements && (
                      <ArrowPathIcon className="w-5 h-5 animate-spin text-primary-600" />
                    )}
                  </div>

                  {loadingMeasurements ? (
                    <div className="text-center py-12">
                      <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
                      <p className="mt-2 text-sm xl:text-base text-gray-600">Havi összesítők betöltése...</p>
                    </div>
                  ) : monthlySummaries.length === 0 ? (
                    <div className="text-center py-12">
                      <CalendarDaysIcon className="w-8 h-8 xl:w-12 xl:h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm xl:text-base text-gray-600">Nincsenek elérhető havi összesítők</p>
                    </div>
                  ) : (
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Hónap
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Havi fogyasztás
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Visszatáplálás
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Havi költség
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Átlagár
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Napi átlag
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Napok száma
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {monthlySummaries.map((summary, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-3 xl:px-4 py-3 text-xs xl:text-sm font-medium text-gray-900">
                                  {summary.year}. {String(summary.monthNumber).padStart(2, '0')}.
                                </td>
                                <td className="px-3 xl:px-4 py-3 text-xs xl:text-sm text-gray-900 font-semibold">
                                  {energiaService.formatKWH(summary.total_consumption_kwh)}
                                </td>
                                <td className="px-3 xl:px-4 py-3 text-xs xl:text-sm">
                                  {summary.total_reverse_feed_kwh > 0 ? (
                                    <div className="flex items-center gap-1">
                                      <span className="text-green-600 font-semibold">
                                        {energiaService.formatKWH(summary.total_reverse_feed_kwh)}
                                      </span>
                                      <span className="text-green-500" title="Van visszatáplálás">☀️</span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-3 xl:px-4 py-3 text-xs xl:text-sm text-gray-900 font-semibold">
                                  {energiaService.formatHUF(summary.total_cost_huf)}
                                </td>
                                <td className="px-3 xl:px-4 py-3 text-xs xl:text-sm text-gray-900">
                                  {energiaService.formatHUF(summary.avg_price_huf_kwh)} / kWh
                                </td>
                                <td className="px-3 xl:px-4 py-3 text-xs xl:text-sm text-gray-600">
                                  {energiaService.formatKWH(summary.daily_avg_consumption)} / nap
                                </td>
                                <td className="px-3 xl:px-4 py-3 text-xs xl:text-sm text-gray-500">
                                  {summary.days_count} nap
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Monthly Summary Stats */}
                      <div className="bg-gray-50 px-4 py-3 border-t">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Összes fogyasztás</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {energiaService.formatKWH(
                                monthlySummaries.reduce((sum, m) => sum + m.total_consumption_kwh, 0)
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Összes visszatáplálás</p>
                            <p className="text-sm font-semibold text-green-600">
                              {energiaService.formatKWH(
                                monthlySummaries.reduce((sum, m) => sum + m.total_reverse_feed_kwh, 0)
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Összes költség</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {energiaService.formatHUF(
                                monthlySummaries.reduce((sum, m) => sum + m.total_cost_huf, 0)
                              )}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Átlagos havi fogyasztás</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {energiaService.formatKWH(
                                monthlySummaries.reduce((sum, m) => sum + m.total_consumption_kwh, 0) / monthlySummaries.length
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Reverse Feed Tab */}
              {activeTab === 'reverse_feed' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base xl:text-lg font-medium text-gray-800 flex items-center gap-2">
                      <span>☀️</span>
                      Visszatáplálási adatok
                    </h3>
                    {loadingMeasurements && (
                      <ArrowPathIcon className="w-5 h-5 animate-spin text-primary-600" />
                    )}
                  </div>

                  {/* Összesítő kártya */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 xl:p-6 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs xl:text-sm text-gray-600 mb-1">Összes visszatáplálás</p>
                        <p className="text-2xl xl:text-3xl font-bold text-green-700">
                          {(selectedPod?.total_reverse_feed_kwh ?? 0) >= 100 
                            ? `${((selectedPod?.total_reverse_feed_kwh ?? 0) / 1000).toFixed(1)}k kWh`
                            : `${(selectedPod?.total_reverse_feed_kwh ?? 0).toFixed(2)} kWh`
                          }
                        </p>
                      </div>
                      <div className="text-5xl">☀️</div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-green-200">
                      <p className="text-xs text-gray-600">
                        Ez az energia, amit a napelem rendszer visszatáplált a hálózatba.
                      </p>
                    </div>
                  </div>

                  {/* Havi bontás - from monthly summaries (accurate data) */}
                  {monthlySummaries.length > 0 && monthlySummaries.some(m => m.total_reverse_feed_kwh > 0) && (
                    <div className="bg-white border rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-800 mb-3">Havi bontás</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {monthlySummaries
                          .filter(m => m.total_reverse_feed_kwh > 0)
                          .map(summary => (
                            <div key={summary.month} className="bg-green-50 rounded-lg p-3 border border-green-200">
                              <div className="text-xs text-gray-600 mb-1">
                                {summary.year}.{String(summary.monthNumber).padStart(2, '0')}
                              </div>
                              <div className="text-sm font-semibold text-green-700 flex items-center gap-1">
                                <span>☀️</span>
                                <span>{energiaService.formatKWH(summary.total_reverse_feed_kwh)}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Visszatáplálási mérések táblázata */}
                  {loadingMeasurements ? (
                    <div className="text-center py-12">
                      <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
                      <p className="mt-2 text-sm xl:text-base text-gray-600">Visszatáplálási adatok betöltése...</p>
                    </div>
                  ) : measurementData.filter(m => (m.reverse_feed && m.reverse_feed > 0) || m.consumption < 0).length === 0 ? (
                    <div className="text-center py-12">
                      <span className="text-6xl mb-4 block">☀️</span>
                      <p className="text-sm xl:text-base text-gray-600">Nincsenek részletes visszatáplálási mérések</p>
                      <p className="text-xs text-gray-500 mt-2">Az összesített visszatáplálás látható fent.</p>
                    </div>
                  ) : (
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-96">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-green-50 sticky top-0">
                            <tr>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Időpont
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Visszatáplálás
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ár
                              </th>
                              <th className="px-3 xl:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Feltöltve
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {measurementData
                              .filter(m => (m.reverse_feed && m.reverse_feed > 0) || m.consumption < 0)
                              .map((measurement, index) => (
                                <tr key={index} className="hover:bg-green-50">
                                  <td className="px-3 xl:px-4 py-2 text-xs xl:text-sm font-medium text-gray-900">
                                    {measurement.timestamp?.toDate ? 
                                      format(measurement.timestamp.toDate(), 'yyyy.MM.dd HH:mm', { locale: hu }) :
                                      'N/A'
                                    }
                                  </td>
                                  <td className="px-3 xl:px-4 py-2 text-xs xl:text-sm text-green-700 font-semibold">
                                    <div className="flex items-center gap-1">
                                      <span>☀️</span>
                                      <span>{(measurement.reverse_feed || Math.abs(measurement.consumption)).toFixed(4)} kWh</span>
                                    </div>
                                  </td>
                                  <td className="px-3 xl:px-4 py-2 text-xs xl:text-sm text-gray-900">
                                    {energiaService.formatHUF(measurement.price_huf_kwh)} / kWh
                                  </td>
                                  <td className="px-3 xl:px-4 py-2 text-xs xl:text-sm text-gray-500">
                                    {measurement.upload_date?.toDate ? 
                                      format(measurement.upload_date.toDate(), 'MM.dd HH:mm', { locale: hu }) :
                                      'N/A'
                                    }
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="bg-green-50 px-4 py-3 border-t border-green-200">
                        <p className="text-xs text-gray-600">
                          Összesen {measurementData.filter(m => (m.reverse_feed && m.reverse_feed > 0) || m.consumption < 0).length} visszatáplálási mérés
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* MSCONS Tab */}
              {activeTab === 'mscons' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base xl:text-lg font-medium text-gray-800 flex items-center gap-2">
                      <span>📊</span>
                      MSCONS Számlázási Adatok
                    </h3>
                  </div>

                  {/* MSCONS billing records táblázat */}
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3">
                      <h4 className="text-white font-medium">Havi Számlázási Összesítők</h4>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Időszak
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Összes fogyasztás
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Nappal (6-17)
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Csúcs (17-22)
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Völgy (22-06)
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Forrás
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {loadingMeasurements ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                                <ArrowPathIcon className="h-5 w-5 animate-spin mx-auto mb-2" />
                                MSCONS adatok betöltése...
                              </td>
                            </tr>
                          ) : msconsBillingRecords.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                                Nincs elérhető MSCONS számlázási adat ehhez a POD-hoz
                                <br />
                                <span className="text-xs text-gray-400 mt-2 block">
                                  Az MSCONS adatok a provider SFTP szerveréről töltődnek be havonta
                                </span>
                              </td>
                            </tr>
                          ) : (
                            msconsBillingRecords.map((record, index) => (
                              <tr key={record.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {record.billing_period_start && record.billing_period_end ? (
                                    <>
                                      {record.billing_period_start.substring(0, 4)}-{record.billing_period_start.substring(4, 6)}-{record.billing_period_start.substring(6, 8)}
                                      {' → '}
                                      {record.billing_period_end.substring(0, 4)}-{record.billing_period_end.substring(4, 6)}-{record.billing_period_end.substring(6, 8)}
                                    </>
                                  ) : 'N/A'}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {record.total_consumption_kwh.toLocaleString('hu-HU', { maximumFractionDigits: 2 })} kWh
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {record.consumption_day_kwh ? 
                                    `${record.consumption_day_kwh.toLocaleString('hu-HU', { maximumFractionDigits: 2 })} kWh` : 
                                    '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {record.consumption_peak_kwh ? 
                                    `${record.consumption_peak_kwh.toLocaleString('hu-HU', { maximumFractionDigits: 2 })} kWh` : 
                                    '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">
                                  {record.consumption_valley_kwh ? 
                                    `${record.consumption_valley_kwh.toLocaleString('hu-HU', { maximumFractionDigits: 2 })} kWh` : 
                                    '-'}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                                    {record.data_source}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500">
                        Összesen {msconsBillingRecords.length} MSCONS számlázási rekord
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 xl:mt-8 flex flex-col xl:flex-row justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                <button
                  onClick={() => setSelectedPod(null)}
                  className="w-full xl:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Bezárás
                </button>
                {selectedPod.customer_info && (
                  <button
                    className="w-full xl:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    CRM ügyfél megtekintése
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnergiaPODs; 